import React, { useState, useMemo } from 'react';

// --- IMPORTACIÓN DE NUESTRAS UTILIDADES DE SEGUROS ---
import { parseCurrency, formatCurrencyUniversal } from '../../../../utils/segurosUtils';

// --- ICONOS LOCALES PREMIUM ---
const IconUser = () => (
    <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
);

const IconShield = () => (
    <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
);

const IconFolder = () => (
    <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
);

const IconBack = () => (
    <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
);

const IconTrendUp = () => (
    <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v6m0-6L10 17l-4-4-6 6" />
    </svg>
);

/**
 * RendimientoVendedores - Analizador visual premium de productores y distribuidores comerciales.
 */
const RendimientoVendedores = ({ registros, isDbLoaded }) => {
    const [selectedVendedor, setSelectedVendedor] = useState('');

    // --- 1. EXTRACCIÓN DINÁMICA DE VENDEDORES ÚNICOS ---
    const listaVendedores = useMemo(() => {
        if (!isDbLoaded || !registros) return [];
        return [...new Set(registros.map(r => r.vendedor))]
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
    }, [registros, isDbLoaded]);

    // --- 2. CUADRO COMPARATIVO GENERAL DE TODOS LOS VENDEDORES ---
    const matrizVendedores = useMemo(() => {
        if (!isDbLoaded || !registros) return [];
        
        const mapVendedores = {};
        registros.forEach(p => {
            const v = p.vendedor || 'SIN ESPECIFICAR';
            if (!mapVendedores[v]) {
                mapVendedores[v] = { nombre: v, totalPolizas: 0, primaARS: 0, primaUSD: 0 };
            }
            mapVendedores[v].totalPolizas += 1;

            const rawPrima = p.primaEmitidaRefacturacion !== undefined ? p.primaEmitidaRefacturacion : p.primaEmitida;
            const prima = parseCurrency(rawPrima);
            const monedaRaw = String(p.moneda || p.monedaRefacturacion || '$').toUpperCase().trim();
            const esUSD = monedaRaw.includes('USD') || monedaRaw.includes('U$S') || monedaRaw.includes('DOLAR');

            if (esUSD) {
                mapVendedores[v].primaUSD += prima;
            } else {
                mapVendedores[v].primaARS += prima;
            }
        });

        // Ordenamos prioritariamente por total de pólizas emitidas (volumen de ventas)
        return Object.values(mapVendedores).sort((a, b) => b.totalPolizas - a.totalPolizas);
    }, [registros, isDbLoaded]);

    // --- 3. CÁLCULO DE DETALLES INDIVIDUALES POR VENDEDOR ---
    const reporte = useMemo(() => {
        if (!isDbLoaded || !selectedVendedor || !registros) {
            return { totalPolizas: 0, totalPrimaARS: 0, totalPrimaUSD: 0, distribucionRiesgos: [], items: [] };
        }

        const polizasDelVendedor = registros.filter(r => r.vendedor === selectedVendedor);
        let totalPrimaARS = 0;
        let totalPrimaUSD = 0;
        const riesgosMap = {};

        polizasDelVendedor.forEach(p => {
            const rawPrima = p.primaEmitidaRefacturacion !== undefined ? p.primaEmitidaRefacturacion : p.primaEmitida;
            const prima = parseCurrency(rawPrima);
            const monedaRaw = String(p.moneda || p.monedaRefacturacion || '$').toUpperCase().trim();
            const esUSD = monedaRaw.includes('USD') || monedaRaw.includes('U$S') || monedaRaw.includes('DOLAR');

            if (esUSD) totalPrimaUSD += prima;
            else totalPrimaARS += prima;

            const r = p.riesgo || 'SIN ESPECIFICAR';
            if (!riesgosMap[r]) riesgosMap[r] = { cant: 0, primaARS: 0, primaUSD: 0 };
            riesgosMap[r].cant += 1;
            if (esUSD) riesgosMap[r].primaUSD += prima;
            else riesgosMap[r].primaARS += prima;
        });

        // Convertimos el map de riesgos en un array y calculamos porcentajes de participación
        const distribucionRiesgos = Object.entries(riesgosMap)
            .map(([key, val]) => {
                const porcentajeParticipacion = polizasDelVendedor.length > 0 
                    ? Math.round((val.cant / polizasDelVendedor.length) * 100) 
                    : 0;
                return { name: key, porcentaje: porcentajeParticipacion, ...val };
            })
            .sort((a, b) => b.porcentaje - a.porcentaje);

        return {
            totalPolizas: polizasDelVendedor.length,
            totalPrimaARS,
            totalPrimaUSD,
            distribucionRiesgos,
            items: polizasDelVendedor
        };
    }, [selectedVendedor, registros, isDbLoaded]);

    if (!isDbLoaded) {
        return (
            <div className="p-12 text-center text-slate-400 italic text-sm bg-white rounded-3xl border border-slate-200/60 shadow-sm max-w-xl mx-auto mt-16 animate-fade-in">
                Cargue un rango de fechas en la cabecera para inicializar el análisis de productores.
            </div>
        );
    }

    if (matrizVendedores.length === 0) {
        return (
            <div className="p-12 text-center text-slate-400 italic text-sm bg-white rounded-3xl border border-slate-200/60 shadow-sm max-w-xl mx-auto mt-16 animate-fade-in">
                No se encontraron transacciones con un vendedor asignado dentro de este período de fechas.
            </div>
        );
    }

    return (
        <div className="space-y-6 text-left animate-fade-in">
            
            {/* ESCENARIO A: CUADRO COMPARATIVO GENERAL DE VENDEDORES */}
            {!selectedVendedor ? (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden text-left flex flex-col animate-fade-in">
                    <div className="p-5 px-6 border-b border-slate-100 flex justify-between items-center text-left bg-gradient-to-r from-slate-50 to-white">
                        <div className="flex items-center gap-3">
                            <span className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl shrink-0">
                                <IconUser />
                            </span>
                            <div>
                                <h4 className="text-sm font-black tracking-tight text-[#01032d] uppercase">Cuadro Comparativo de Vendedores</h4>
                                <p className="text-[10px] text-slate-400 font-bold mt-0.5 tracking-wider uppercase">Orden de rendimiento por volumen de emisión</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="overflow-x-auto w-full">
                        <table className="w-full text-xs border-collapse text-left">
                            <thead className="bg-slate-50/70 text-slate-400 border-b border-slate-100 uppercase text-[9px] font-black tracking-wider">
                                <tr>
                                    <th className="p-4 pl-6 w-16 text-center">Rango</th>
                                    <th className="p-4">Productor / Asesor</th>
                                    <th className="p-4 text-center">Pólizas</th>
                                    <th className="p-4 text-right">Prima Emitida (ARS)</th>
                                    <th className="p-4 text-right pr-6">Prima Emitida (USD)</th>
                                </tr>
                            </thead>
                            <tbody className="font-semibold text-slate-700 divide-y divide-slate-100 bg-white">
                                {matrizVendedores.map((v, index) => {
                                    const rank = index + 1;
                                    return (
                                        <tr 
                                            key={v.nombre} 
                                            onClick={() => setSelectedVendedor(v.nombre)}
                                            className="hover:bg-blue-50/30 cursor-pointer transition-colors duration-150 group"
                                        >
                                            {/* Podio Gamificado */}
                                            <td className="p-4 pl-6 text-center">
                                                {rank === 1 && (
                                                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-50 border border-amber-200 text-amber-600 font-black text-[10px]" title="Primer Puesto">
                                                        🥇
                                                    </span>
                                                )}
                                                {rank === 2 && (
                                                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 border border-slate-200 text-slate-600 font-black text-[10px]" title="Segundo Puesto">
                                                        🥈
                                                    </span>
                                                )}
                                                {rank === 3 && (
                                                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-50 border border-orange-200 text-orange-600 font-black text-[10px]" title="Tercer Puesto">
                                                        🥉
                                                    </span>
                                                )}
                                                {rank > 3 && (
                                                    <span className="text-[10px] font-mono text-slate-400 font-bold">
                                                        #{rank}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="p-4 text-slate-800 font-bold group-hover:text-blue-900 transition-colors uppercase">
                                                {v.nombre}
                                            </td>
                                            <td className="p-4 text-center font-mono font-bold text-slate-600">
                                                {v.totalPolizas}
                                            </td>
                                            <td className="p-4 text-right font-mono text-emerald-600 font-bold">
                                                {v.primaARS > 0 ? formatCurrencyUniversal(v.primaARS, '$') : '-'}
                                            </td>
                                            <td className="p-4 text-right font-mono text-emerald-600 pr-6 font-bold">
                                                {v.primaUSD > 0 ? formatCurrencyUniversal(v.primaUSD, 'U$S') : '-'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                
                /* ESCENARIO B: VISTA DE LA FICHA INDIVIDUAL DEL PRODUCTOR */
                <div className="space-y-6 animate-fade-in">
                    
                    {/* CONTROL Y ENCABEZADO DE RETORNO */}
                    <div className="bg-white p-4 px-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                        <div className="flex items-center gap-4">
                            <button 
                                onClick={() => setSelectedVendedor('')}
                                className="group flex items-center gap-2 p-2.5 px-4 bg-slate-100 hover:bg-slate-200/80 text-slate-700 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-200 active:scale-95"
                            >
                                <IconBack /> Volver al Resumen
                            </button>
                            <div className="border-l pl-4 border-slate-200">
                                <h3 className="text-md font-black text-[#01032d] uppercase tracking-tight">Ecosistema de Cartera: {selectedVendedor}</h3>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Participación comercial, ramos y deudas asociadas</p>
                            </div>
                        </div>
                    </div>

                    {/* CARTAS DE KPI PREMIUM CON MONEDA EN VERDE */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        <div className="bg-white p-5 border border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 flex justify-between items-center">
                            <div>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Ventas Totales</p>
                                <h4 className="text-2xl font-black text-slate-800 mt-2 font-mono">{reporte.totalPolizas}</h4>
                            </div>
                            <span className="p-3 bg-slate-50 border border-slate-100 text-[#01032d] rounded-xl shrink-0 w-11 h-11 flex items-center justify-center font-mono font-black text-xs">#</span>
                        </div>

                        <div className="bg-white p-5 border border-emerald-100 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 flex justify-between items-center bg-gradient-to-br from-emerald-50/10 to-white">
                            <div>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Prima Recaudada (ARS)</p>
                                <h4 className="text-2xl font-black text-emerald-600 mt-2 font-mono">
                                    {formatCurrencyUniversal(reporte.totalPrimaARS, '$')}
                                </h4>
                            </div>
                            <span className="p-3 bg-emerald-50 text-emerald-600 rounded-xl shrink-0 w-11 h-11 flex items-center justify-center">
                                <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v6m0-6L10 17l-4-4-6 6" />
                                </svg>
                            </span>
                        </div>

                        <div className="bg-white p-5 border border-emerald-100 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 flex justify-between items-center bg-gradient-to-br from-emerald-50/10 to-white">
                            <div>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Prima Recaudada (USD)</p>
                                <h4 className="text-2xl font-black text-blue-600 mt-2 font-mono">
                                    {formatCurrencyUniversal(reporte.totalPrimaUSD, 'U$S')}
                                </h4>
                            </div>
                            <span className="p-3 bg-blue-50 text-blue-600 rounded-xl shrink-0 w-11 h-11 flex items-center justify-center font-mono font-black text-xs">USD</span>
                        </div>
                    </div>

                    {/* SECCIONES: DISTRIBUCIÓN POR RAMOS Y CARTERA DE CLIENTES */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        
                        {/* COLUMNA 1: DISTRIBUCIÓN POR RIESGOS (PRIMAS POR RIESGO Y CONTEOS SIN BARRA DE PORCENTAJE) */}
                        <div className="bg-white rounded-2xl border overflow-hidden lg:col-span-1 shadow-sm flex flex-col">
                            <div className="p-4 bg-slate-50 border-b font-black text-[10px] text-slate-500 uppercase tracking-wider flex items-center gap-2">
                                <IconShield /> Mix de Cartera (Riesgos)
                            </div>
                            
                            <div className="p-4 space-y-3 max-h-[380px] overflow-y-auto custom-scrollbar flex-grow bg-white">
                                {reporte.distribucionRiesgos.length === 0 ? (
                                    <p className="text-xs text-slate-400 italic text-center py-8">Sin datos de riesgos</p>
                                ) : (
                                    reporte.distribucionRiesgos.map(dr => (
                                        <div key={dr.name} className="p-3 bg-slate-50 hover:bg-slate-100/50 rounded-xl border border-slate-100 transition-colors flex justify-between items-center text-xs">
                                            <div className="flex flex-col text-left min-w-0">
                                                <span className="uppercase text-slate-800 font-black truncate">{dr.name}</span>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase mt-0.5">
                                                    {dr.cant} {dr.cant === 1 ? 'Póliza' : 'Pólizas'}
                                                </span>
                                            </div>
                                            <div className="flex flex-col items-end gap-1 font-mono text-[11px] font-bold shrink-0 pl-3">
                                                {dr.primaARS > 0 && <span className="text-emerald-600">{formatCurrencyUniversal(dr.primaARS, '$')}</span>}
                                                {dr.primaUSD > 0 && <span className="text-emerald-600">{formatCurrencyUniversal(dr.primaUSD, 'U$S')}</span>}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* COLUMNA 2 Y 3: CARTERA DE ASEGURADOS DETALLADA */}
                        <div className="bg-white rounded-2xl border overflow-hidden lg:col-span-2 shadow-sm flex flex-col">
                            <div className="p-4 bg-slate-50 border-b font-black text-[10px] text-slate-500 uppercase tracking-wider flex items-center gap-2">
                                <IconFolder /> Asegurados Asociados al Productor ({reporte.items.length})
                            </div>
                            
                            <div className="overflow-auto max-h-[380px] custom-scrollbar text-xs flex-grow bg-white">
                                <table className="w-full text-left font-medium border-collapse min-w-[600px] table-fixed">
                                    <thead className="bg-slate-50 text-slate-400 font-black border-b uppercase text-[9px] tracking-wider sticky top-0 z-10 shadow-[0_1px_0_0_rgba(226,232,240,1)]">
                                        <tr>
                                            <th className="p-3 pl-4 w-1/3 text-left">Asegurado</th>
                                            <th className="p-3 text-center">N° Póliza</th>
                                            <th className="p-3 text-center">Compañía</th>
                                            <th className="p-3 text-center">Riesgo</th>
                                            <th className="p-3 text-right pr-6 w-32">Prima</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 text-slate-600 font-semibold">
                                        {reporte.items.map((item, i) => {
                                            const isUSD = String(item.moneda || '$').toUpperCase().includes('USD');
                                            const rawPrima = item.primaEmitidaRefacturacion !== undefined ? item.primaEmitidaRefacturacion : item.primaEmitida;
                                            
                                            return (
                                                <tr key={item.id || i} className="hover:bg-slate-50/40 transition-colors duration-150">
                                                    <td className="p-3 pl-4 font-bold text-slate-800 truncate text-left">
                                                        {item.clienteNombre} {item.clienteApellido}
                                                    </td>
                                                    <td className="p-3 font-mono font-bold text-slate-700 text-center">
                                                        {item.nPoliza || 'PENDIENTE'}
                                                    </td>
                                                    <td className="p-3 uppercase text-center truncate text-[11px]">{item.compania}</td>
                                                    <td className="p-3 uppercase text-center truncate text-[11px]">{item.riesgo}</td>
                                                    {/* Formato financiero uniforme con segurosUtils e indicador de color verde */}
                                                    <td className="p-3 text-right pr-6 font-mono font-bold text-emerald-600">
                                                        {formatCurrencyUniversal(rawPrima, isUSD ? 'U$S' : '$')}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                    </div>
                </div>
            )}

            {/* SOPORTE DE SCROLLBARS Y TRANSICIONES */}
            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .animate-fade-in { animation: fadeIn 0.25s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default RendimientoVendedores;